import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Target, Zap, ArrowRight, Menu, X } from 'lucide-react';
import './InfoPage.css';

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="info-page">
      {/* Navigation */}
      <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} className="info-nav">
        <div className="nav-container">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src="/homequest-logo.svg" alt="HomeQuest Tech" className="logo-image" />
            <div className="logo-tagline">Where Land Meets Logic</div>
          </div>

          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a onClick={() => navigate('/')}>Home</a>
            <a onClick={() => navigate('/features')}>Features</a>
            <a onClick={() => navigate('/pricing')}>Pricing</a>
            <button onClick={() => navigate('/login')} className="nav-login">Sign In</button>
            <button onClick={() => navigate('/pricing')} className="nav-cta">Start Free Trial</button>
          </div>

          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </motion.nav>

      {/* Hero */}
      <section className="info-hero">
        <div className="hero-container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="hero-badge">
              <Building2 className="badge-icon" />
              <span>ABOUT HOMEQUEST</span>
            </div>
            <h1 className="hero-title">
              Where Land <span className="gradient-text">Meets Logic</span>
            </h1>
            <p className="hero-subtitle">
              We're revolutionizing construction management with AI-powered automation that handles the complexity, so you can focus on building.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="info-content">
        <div className="content-container">
          <h2>Our Mission</h2>
          <p>
            HomeQuest Tech is on a mission to transform the construction industry through intelligent automation. We believe that builders, contractors, and project managers shouldn't be buried in paperwork, chasing vendors, or manually processing data. Our AI-powered platform handles the administrative burden, letting you focus on what you do best: building exceptional projects.
          </p>

          <h2>The Problem We're Solving</h2>
          <p>
            Construction projects are incredibly complex, involving coordination between dozens of stakeholders, managing countless documents, tracking budgets across multiple phases, and ensuring compliance with local regulations. Traditional project management tools force you to do all this manually.
          </p>
          <p>
            We asked: What if AI could do the heavy lifting? What if your platform could automatically identify rooms from CAD files, calculate setbacks from GIS county data, schedule vendors based on lead times, and even follow up with calls if they don't respond to messages?
          </p>

          <h2>Our Solution</h2>
          <p>
            HomeQuest Tech is the first truly intelligent construction management platform. Our AI doesn't just assist you—it actively manages your projects:
          </p>
          <ul>
            <li><strong>AI Floor Plan Processing:</strong> Upload CAD files and watch AI automatically detect rooms and place your selections</li>
            <li><strong>GIS-Integrated Site Analysis:</strong> Direct county system integration for instant setback calculations and zoning compliance</li>
            <li><strong>AI Communication Assistant:</strong> Handles calls, schedules appointments, composes emails, and manages vendor coordination</li>
            <li><strong>Smart Vendor Management:</strong> Sends bid requests with CAD files, compares bids, and auto-schedules based on historical performance</li>
            <li><strong>Intelligent Phase Tracking:</strong> AI monitors phases, analyzes invoices, and automatically coordinates vendor scheduling</li>
            <li><strong>Photo Intelligence:</strong> Take site photos and get instant AI-generated notes and issue detection</li>
          </ul>

          <h2>Why We're Different</h2>
          <p>
            Unlike traditional construction software that simply digitizes paperwork, HomeQuest Tech uses AI to eliminate the work entirely. We don't just store your data—we understand it, act on it, and proactively manage your projects.
          </p>

          <h2>Join Our Beta Program</h2>
          <p>
            We're currently offering exclusive access to our beta program for 10 founding members. Lock in 30% off for life and help shape the future of construction management.
          </p>

          <button onClick={() => navigate('/pricing')} className="cta-button">
            Join Beta Program <ArrowRight style={{ width: '20px', height: '20px' }} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="info-footer">
        <div className="footer-container">
          <div className="footer-content">
            <p>&copy; 2025 HomeQuest Tech™. All rights reserved.</p>
            <div className="footer-links">
              <a onClick={() => navigate('/')}>Home</a>
              <a onClick={() => navigate('/features')}>Features</a>
              <a onClick={() => navigate('/pricing')}>Pricing</a>
              <a onClick={() => navigate('/contact')}>Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
